import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { GAME_CONFIG } from '../config/gameConfig';
import { MUSIC_BPM, BEATMAPS } from '../config/beatmaps';
import { Timestamp } from 'firebase/firestore';
import { toKstIso } from '../utils/firestoreApi';

const GameContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const GameArea = styled.div`
  width: 100%;
  height: 100%;
  max-width: 400px;
  /* aspect-ratio: 9/16; */
  /* background: rgba(0, 0, 0, 0.8); */
  border-radius: 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;

  touch-action: none;
  overscroll-behavior: none;
  -webkit-user-select: none;
  user-select: none;
  
  @media (max-width: 768px) {
    max-width: 355px;
    max-height: 452px;
    /* aspect-ratio: 9/14; */
  }
`;

const CanvasContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
`;

const GameCanvas = styled.canvas`
  width: 100%;
  height: 100%;
  display: block;
`;

const LaneContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  pointer-events: none;
  touch-action: none;
  overscroll-behavior: none;
  -webkit-user-select: none;
  user-select: none;
`;

const Lane = styled.div`
  flex: 1;
  /* border-right: 2px solid rgba(255, 255, 255, 0.3); */
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  pointer-events: auto;
  touch-action: none;
  overscroll-behavior: none;
  -webkit-user-select: none;
  user-select: none;
  &:last-child {
    border-right: none;
  }
`;

const KeyLabel = styled.div`
  position: absolute;
  bottom: 70px;
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  font-size: 1.2rem;
  opacity: 0.5;
  @media (pointer: coarse) {
    display: none;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    bottom: 60px;
  }
`;

const comboAppear = keyframes`
  0%   { transform: translate(-50%, -50%) scale(0.6); opacity: 0; }
  40%  { transform: translate(-50%, -50%) scale(1.2); opacity: 1; }
  100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
`;

const CurrentCombo = styled.div`
  position: absolute;
  top: 44%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.0rem;
  color: #00eaff;
  font-weight: bold;
  text-shadow: 0 0 10px #000;
  z-index: 10;
  pointer-events: none;
  animation: ${comboAppear} 0.4s ease-out;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    top: 42%;
  }
`;

const RoundInfo = styled.div`
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  font-size: 1.2rem;
  font-weight: bold;
  text-shadow: 0 0 10px #000;
  z-index: 10;
  pointer-events: none;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    top: 15px;
  }
`;

const ModeInfo = styled.div`
  position: absolute;
  top: 50px;
  left: 50%;
  transform: translateX(-50%);
  color: #ffd700;
  font-size: 1rem;
  font-weight: bold;
  text-shadow: 0 0 10px #000;
  z-index: 10;
  pointer-events: none;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    top: 40px;
  }
`;

const judgeAppear = keyframes`
  0%   { transform: translate(-50%, -50%) scale(0.8); opacity: 0; }
  40%  { transform: translate(-50%, -50%) scale(1.2); opacity: 1; }
  100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
`;

const judgeColors = {
  Perfect: '#00ffe1',
  Good: '#ffff66',
  Miss: '#ff3b3b',
};

const JudgeMessage = styled.div`
  position: absolute;
  top: 40%;
  left: 50%;
  text-shadow: 2px 2px 6px rgba(0, 0, 0, 0.7);
  letter-spacing: 0.05em;
  font-weight: bold;
  transform: translate(-50%, -50%);
  font-size: 2.4rem;
  color: ${({ result }) => judgeColors[result] || '#fff'};
  text-shadow:
    0 0 5px ${({ result }) => judgeColors[result] || '#fff'},
    0 0 10px ${({ result }) => judgeColors[result] || '#fff'},
    0 0 20px ${({ result }) => judgeColors[result] || '#fff'};
  white-space: nowrap;
  pointer-events: none;
  z-index: 20;
  animation: ${judgeAppear} 0.4s ease-out;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    top: 35%;
  }
`;

const ResultScreen = styled.div`
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.85);
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const ResultTitle = styled.h2`
  font-size: 2.2rem;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 15px;
  }
`;

const ResultItem = styled.p`
  font-size: 1.7rem;
  margin: 8px 0;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin: 6px 0;
  }
`;

const keyLabels = GAME_CONFIG.KEY_LABELS;
const keyMap = GAME_CONFIG.KEY_MAP;

const BottomSpacer = styled.div`
  width: 100%;
  height: 80px;
  @media (max-width: 768px) {
    height: 60px;
  }
`;

const RhythmGame = React.forwardRef(({ 
  onGameEnd, 
  currentTimeRef,
  gameMode = 'normal', 
  currentRound = 1, 
  totalRounds = 1, 
  music = '/PURPLEKISS.mp3', 
  beatmap, 
  shouldPreloadAudio, 
  onAudioPreloaded, 
  audioController }, ref) => {

  const [score, setScore] = React.useState(0);
  const [combo, setCombo] = React.useState(0);
  const [maxCombo, setMaxCombo] = React.useState(0);
  const [judgeMessage, setJudgeMessage] = React.useState('');
  const [judgeKey, setJudgeKey] = React.useState(0);
  const [showResult, setShowResult] = React.useState(false);
  const [showCombo, setShowCombo] = useState(true);
  const [activeLanes, setActiveLanes] = useState(new Set()); // 멀티터치 지원
  const [audioReady, setAudioReady] = useState(false);

  // ref로 preloadAudio 함수 노출
  // React.useImperativeHandle(ref, () => ({
  //   preloadAudio: async () => {
  //     try {
  //       await audioController.current.init(backgroundMusic);
  //       setAudioReady(true);
  //       if (onAudioPreloaded) onAudioPreloaded();
  //     } catch (error) {
  //       console.error('Audio preload failed:', error);
  //     }
  //   },
  //   unlockAudio: () => {
  //     // PLAY 터치 시 오디오 컨텍스트 해제
  //     try {
  //       audioController.current.resume();
  //       console.log('Audio context unlocked on PLAY touch');
  //     } catch (error) {
  //       console.error('Audio unlock failed:', error);
  //     }
  //   }
  // }));

  const canvasRef = React.useRef(null);
  const animationRef = React.useRef(null);
  const gameAreaRef = React.useRef(null);
  //const audioController = React.useRef(new AudioController());
  const judgeTimeout = React.useRef(null);
  const hitNotesRef = React.useRef(new Set());
  const missedNotesRef = React.useRef(new Set());
  const comboRef = React.useRef(0);
  const [canvasWidth, setCanvasWidth] = React.useState(400);
  const [canvasHeight, setCanvasHeight] = React.useState(640);
  const scoreRef = React.useRef(0);
  const maxComboRef = React.useRef(0);
  const activeTouchesRef = React.useRef(new Map()); // 터치 ID별 레인 추적
  const groupedNotesRef = React.useRef({}); // 그룹별 노트 관리
  // 터치된 그룹별로 터치한 레인을 기록할 임시 저장소 추가
  const groupTouchedLanesRef = React.useRef({});
  const groupTimeoutsRef = React.useRef({});
  const laneQueuesRef = React.useRef({});
  const perfectGroupsRef = React.useRef(0); // Perfect 그룹 개수 추적

  // Config에서 값 가져오기
  const NOTE_TRAVEL_TIME = GAME_CONFIG.calculateNoteTravelTime(MUSIC_BPM);
  const JUDGMENT_WINDOWS = GAME_CONFIG.JUDGMENT_WINDOWS;
  const ROUND_MAX_SCORES = GAME_CONFIG.ROUND_MAX_SCORES;

  // 현재 게임 모드에 따른 판정 윈도우 적용
  const { perfect, good, miss } = JUDGMENT_WINDOWS[gameMode];

  const PERFECT_WINDOW = perfect;
  const GOOD_WINDOW = good;
  const MISS_WINDOW = miss;

  const backgroundMusic = music;
  const currentBeatmap = beatmap;

  
  // 그룹별 노트 초기화 (게임 시작 시)
  useEffect(() => {
      const map = {};
      currentBeatmap.forEach(note => {
        // note.time 이 정확히 같은 노트들끼리 묶음
        const key = note.time;
        if (!map[key]) map[key] = [];
        map[key].push(note);
      });
      groupedNotesRef.current = map;
    }, [currentBeatmap]);

  // 라운드별 최대 점수
  const maxScoreForRound = ROUND_MAX_SCORES[currentRound] || 3000;
  // 그룹(동일 시간 노트) 개수 계산
  const groupTimes = React.useMemo(() => Array.from(new Set(currentBeatmap.map(note => note.time))), [currentBeatmap]);
  const groupCount = groupTimes.length;

  // baseScore를 그룹 개수와 콤보 보너스를 고려해서 역산
  let sum = 0;
  for (let combo = 1; combo <= groupCount; combo++) {
    sum += (1 + combo / 100);
  }
  // 초기 baseScore 계산
  const baseScore = maxScoreForRound / sum;
  // console.log('ROUND_MAX_SCORES', ROUND_MAX_SCORES);
  // console.log('gameMode', gameMode);
  // console.log('maxScoreForRound', ROUND_MAX_SCORES[gameMode]);


  // console.log('🎯 점수 계산 디버깅:');
  // console.log('▶ groupCount:', groupCount);
  // console.log('▶ maxScoreForRound:', maxScoreForRound);
  // console.log('▶ baseScore:', baseScore);

  // 레인별 큐 초기화
  useEffect(() => {
    const queues = { 0: [], 1: [], 2: [], 3: [] };
    currentBeatmap.forEach(note => {
      queues[note.lane].push(note.time);
    });
    // 오름차순 정렬
    Object.values(queues).forEach(arr => arr.sort((a, b) => a - b));
    laneQueuesRef.current = queues;
  }, [currentBeatmap]);


  // 점수/콤보 업데이트 시 ref도 같이 갱신
  // setScore, setMaxCombo 호출부에 아래와 같이 추가되어야 함
  // 예시: setScore(s => { scoreRef.current = s + 100; return scoreRef.current; });
  // 예시: setMaxCombo(c => { maxComboRef.current = Math.max(c, newCombo); return maxComboRef.current; });

  // 캔버스 크기 동기화 (모바일은 355x500 고정, dpr 적용)
  React.useEffect(() => {
    const updateCanvasSize = () => {
      if (!gameAreaRef.current || !canvasRef.current) return;
      const dpr = window.devicePixelRatio || 1;
      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        // 모바일: 355x452 고정
        canvasRef.current.width = 355 * dpr;
        canvasRef.current.height = 452 * dpr;
        canvasRef.current.style.width = '355px';
        canvasRef.current.style.height = '452px';
        const ctx = canvasRef.current.getContext('2d');
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr);
        setCanvasWidth(355);
        setCanvasHeight(452);
      } else {
        // 데스크탑: GameArea 크기 기준
        const rect = gameAreaRef.current.getBoundingClientRect();
        canvasRef.current.width = rect.width * dpr;
        canvasRef.current.height = rect.height * dpr;
        canvasRef.current.style.width = `${rect.width}px`;
        canvasRef.current.style.height = `${rect.height}px`;
        const ctx = canvasRef.current.getContext('2d');
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr);
        setCanvasWidth(rect.width);
        setCanvasHeight(rect.height);
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    window.addEventListener('orientationchange', updateCanvasSize);
    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      window.removeEventListener('orientationchange', updateCanvasSize);
    };
  }, []);

  // 게임 시작/종료 관리
  React.useEffect(() => {
    if (showResult) return;
    let stopped = false;
    const startGame = async () => {
      // 오디오가 준비되지 않았으면 초기화
      if (!audioReady) {
        try {
          setAudioReady(true);
        } catch (error) {
          // 에러는 AudioController에서 처리하므로 여기서는 무시
        }
      }
      
      // 카운트다운 후 재생했으니까 다시 재생 X
      // 게임 시작 시 오디오 재생 시도 (iOS에서 터치 이벤트 유지)
      // try {
      //   //console.log('게임 시작 - 오디오 재생 시도');
      //   //await audioController.unlock();
      //   await audioController.play();
      //   //console.log('게임 시작 - 오디오 재생 성공');
      // } catch (error) {
      //   //console.warn('게임 시작 - 오디오 재생 실패:', error);
      // }
      
      hitNotesRef.current.clear();
      missedNotesRef.current.clear();
      setScore(0);
      setCombo(0);
      setMaxCombo(0);
      comboRef.current = 0;
      perfectGroupsRef.current = 0; // Perfect 그룹 개수 초기화
      setShowResult(false);
      gameLoop();
    };
    startGame();
    async function gameLoop() {
      if (stopped || showResult) return;
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) {
        //console.log('Canvas context not ready, retrying...');
        animationRef.current = requestAnimationFrame(() => gameLoop());
        return;
      }
      const canvasW = canvasWidth;
      const canvasH = canvasHeight;
      const gameTime = audioController.getCurrentTime() * 1000; // 초를 밀리초로 변환
      const notes = currentBeatmap;
    
      // ✅ 판정선 위치 (게임 영역의 2/3 지점)
      const hitZoneY = canvasH * (2 / 3);
      const pxPerMs  = hitZoneY / NOTE_TRAVEL_TIME;
    
      // ✅ 캔버스 초기화 + 배경
      ctx.clearRect(0, 0, canvasW, canvasH);
      ctx.fillStyle = 'rgba(0,0,0,0.8)';
      ctx.fillRect(0, 0, canvasW, canvasH);
    
      // ✅ 판정영역 배경 시각화 (판정선부터 아래 전체)
      ctx.save();
      // 강렬한 빨간색 그라데이션 배경
      const gradient = ctx.createLinearGradient(0, hitZoneY, 0, canvasH);
      gradient.addColorStop(0, 'rgb(75, 75, 75)');
      gradient.addColorStop(1, 'rgb(49, 49, 49)');
      //gradient.addColorStop(1, 'rgba(228, 228, 228, 0.4)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, hitZoneY, canvasW, canvasH - hitZoneY);
      
      // 추가적인 강조 효과 - 빨간색 테두리
      // ctx.strokeStyle = 'rgba(255, 0, 0, 0.9)';
      // ctx.lineWidth = 3;
      // ctx.strokeRect(0, hitZoneY, canvasW, canvasH - hitZoneY);
      
      // 내부 하이라이트 효과
      ctx.globalAlpha = 0.3;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.fillRect(0, hitZoneY + 5, canvasW, 10);
      ctx.globalAlpha = 1;
      ctx.restore();
    
      // ✅ 레인 구분선
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.66)';
      ctx.lineWidth = 2;
      for (let i = 1; i < 4; i++) {
        const x = (canvasW / 4) * i;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvasH);
        ctx.stroke();
      }
    
      // ✅ 판정선 강조 (시각 효과만)
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(0, hitZoneY);
      ctx.lineTo(canvasW, hitZoneY);
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#fff';
      //ctx.shadowColor = 'rgba(255, 0, 0, 0.69)';
      //ctx.shadowBlur = 10;
      ctx.stroke();
      ctx.closePath();
      ctx.restore();


      // // ——— 판정 밴드 그리기 (확인용) ———
      // ctx.save();
      // // 넓이 구하기
      // const perfectPx = PERFECT_WINDOW * pxPerMs;
      // const goodPx    = GOOD_WINDOW    * pxPerMs;
      // const missPx    = MISS_WINDOW    * pxPerMs;

      // // Miss 영역 (가장 넓은 밴드)
      // ctx.fillStyle = 'rgba(255,59,59,0.2)';
      // ctx.fillRect(0, hitZoneY - missPx, canvasW, missPx * 2);

      // // Good 영역
      // ctx.fillStyle = 'rgba(255,255,102,0.2)';
      // ctx.fillRect(0, hitZoneY - goodPx, canvasW, goodPx * 2);

      // // Perfect 영역
      // ctx.fillStyle = 'rgba(0,255,225,0.2)';
      // ctx.fillRect(0, hitZoneY - perfectPx, canvasW, perfectPx * 2);

      // // 가운데 Hit Line
      // ctx.strokeStyle = '#fff';
      // ctx.lineWidth = 2;
      // ctx.beginPath();
      // ctx.moveTo(0, hitZoneY);
      // ctx.lineTo(canvasW, hitZoneY);
      // ctx.stroke();
      // ctx.restore();
    
      const laneWidth = canvasW / 4;
      
        // ✅ 노트 처리 및 렌더링
        notes.forEach(note => {
          const noteId = `${note.time}-${note.lane}`;
          if (hitNotesRef.current.has(noteId) || missedNotesRef.current.has(noteId)) return;


          const appearTime = note.time - NOTE_TRAVEL_TIME;
          const progress = (gameTime - appearTime) / NOTE_TRAVEL_TIME;
          const pxPerMs = hitZoneY / NOTE_TRAVEL_TIME;
          
          // 노트 미처리 시 미스 처리
          if (gameTime > note.time + MISS_WINDOW) {
            // missedNotesRef.current.add(noteId);
            // setCombo(0);
            // comboRef.current = 0;
            // showJudge('Miss', note);
            evaluateGroup(note.time, [note]);
          }
          // 노트 렌더링
          let noteTop = progress * hitZoneY;
          if (gameTime > note.time) {
            noteTop = hitZoneY + (gameTime - note.time) * pxPerMs;
          }

          const x = laneWidth * note.lane + laneWidth * 0.1;
          const width = laneWidth * 0.8;
          const height = 20 * (canvasH / canvasHeight);
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(x + 10, noteTop);
          ctx.lineTo(x + width - 10, noteTop);
          ctx.quadraticCurveTo(x + width, noteTop, x + width, noteTop + 10);
          ctx.lineTo(x + width, noteTop + height - 10);
          ctx.quadraticCurveTo(x + width, noteTop + height, x + width - 10, noteTop + height);
          ctx.lineTo(x + 10, noteTop + height);
          ctx.quadraticCurveTo(x, noteTop + height, x, noteTop + height - 10);
          ctx.lineTo(x, noteTop + 10);
          ctx.quadraticCurveTo(x, noteTop, x + 10, noteTop);
          ctx.closePath();
          ctx.shadowColor = '#ff6b6b';
          ctx.shadowBlur = 0; // ✅ 선명도 개선
          ctx.fillStyle = '#ff6b6b';
          ctx.fill();
          ctx.lineWidth = 2; // ✅ 외곽선 강조
          ctx.strokeStyle = '#ffaaaa'; // ✅ 연한 외곽선 색 추가
          ctx.stroke();
          ctx.shadowBlur = 0; // ✅ 마지막에 초기화만
          ctx.restore();
        });

        // 그룹 노트 처리
        // Object.entries(groupedNotesRef.current).forEach(([groupId, group]) => {
        //   const allProcessed = group.every(note => {
        //     const noteId = `${note.time}-${note.lane}`;
        //     return hitNotesRef.current.has(noteId) || missedNotesRef.current.has(noteId);
        //   });
        //   if (allProcessed) return;
  
        //   const allMissed = group.every(note => {
        //     const noteId = `${note.time}-${note.lane}`;
        //     return (
        //       !hitNotesRef.current.has(noteId) &&
        //       !missedNotesRef.current.has(noteId) &&
        //       gameTime > note.time + MISS_WINDOW
        //     );
        //   });
  
        //   if (allMissed) {
        //     group.forEach(note => {
        //       const noteId = `${note.time}-${note.lane}`;
        //       missedNotesRef.current.add(noteId);
        //     });
  
        //     setCombo(0);
        //     comboRef.current = 0;
        //     showJudge('Miss', group[0]);
        //     delete groupTouchedLanesRef.current[groupId];
        //   }
        // });

        /// evaluateGroup 사용으로 변경
        Object.entries(groupedNotesRef.current).forEach(([groupId, group]) => {
          const key = Number(groupId);
          const allProcessed = group.every(note => hitNotesRef.current.has(`${note.time}-${note.lane}`) || missedNotesRef.current.has(`${note.time}-${note.lane}`));
          if (allProcessed) return;

          const allMissed = group.every(note =>
            !hitNotesRef.current.has(`${note.time}-${note.lane}`) &&
            !missedNotesRef.current.has(`${note.time}-${note.lane}`) &&
            gameTime > note.time + MISS_WINDOW
          );
          if (allMissed) {
            // 스트럼 스타일 자동 Miss
            evaluateGroup(key, group);
          }
        });
        
      // 🔆 레인 클릭 효과 강조 (멀티터치 지원)
      if (activeLanes.size > 0) {
        activeLanes.forEach(laneIndex => {
          const x = laneWidth * laneIndex;
          ctx.save();
          ctx.fillStyle = 'rgba(255,255,255,0.12)'; // 연한 흰색
          ctx.fillRect(x, 0, laneWidth, canvasHeight);
          ctx.restore();
        });
      }
      // 게임 종료(음악 끝 or 마지막 노트 + 여유시간)

      const lastNote = currentBeatmap[currentBeatmap.length - 1];
      const gameEndTime = lastNote.time + GAME_CONFIG.GAME_END_BUFFER;
      
      // 오디오가 중단되었지만 게임이 끝나지 않았으면 재시도
      // if (!audioController.isPlaying && gameTime < gameEndTime) {
      //   try {
      //     await audioController.play();
      //   } catch (error) {
      //     // 에러는 AudioController에서 처리하므로 여기서는 무시
      //   }
      // }
      
      // 게임이 끝났을 때만 종료 (오디오 재생 상태와 무관하게)
      if (gameTime >= gameEndTime) {
        if (!showResult) {
          // 최종 점수 보정: 모든 그룹에 Perfect일 때만 최대 점수
          let finalScore = scoreRef.current;
          if (perfectGroupsRef.current === groupCount) {
            finalScore = maxScoreForRound;
            //console.log('🎯 모든 그룹 Perfect! 최대 점수 적용:', finalScore);
          }
          
          // ref의 최신값을 넘김
          onGameEnd({
            score: finalScore,
            combo: maxComboRef.current,
            timestamp: toKstIso(Timestamp.now())  
          });
          setShowResult(true);
          
          // 노트가 끝나면 음악도 정지
          //console.log('게임 종료 - 음악 정지');
          audioController.stop();
        }
        return;
      }
      animationRef.current = requestAnimationFrame(() => gameLoop());
    }
    return () => {
      stopped = true;
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      // 컴포넌트가 언마운트될 때만 오디오 정리 (게임 중에는 계속 재생)
      if (judgeTimeout.current) clearTimeout(judgeTimeout.current);
      // audioController.destroy(); // 게임 중에는 오디오를 파괴하지 않음

    };
      }, [showResult, canvasWidth, canvasHeight]);

  // 판정 메시지
  const lastNoteTime = currentBeatmap[currentBeatmap.length - 1].time;
  const showJudge = useCallback((msg, note, delta) => {
    // // delta는 +면 늦게, -면 빨리 입력된 값 (테스트옹)
    // const text = `${msg} (${delta >= 0 ? '+' : ''}${Math.round(delta)}ms)`;
    // setJudgeMessage(text);
    
    setJudgeMessage(msg);
    setJudgeKey(prev => prev + 1);
    if (judgeTimeout.current) clearTimeout(judgeTimeout.current);
    judgeTimeout.current = setTimeout(() => {
      setJudgeMessage(current => (current === msg ? '' : current));
      // 마지막 노트의 판정이면 콤보도 같이 사라지게
      if (note && note.time === lastNoteTime) {
        setShowCombo(false);
      }
    }, GAME_CONFIG.JUDGE_MESSAGE_DURATION);
  }, [lastNoteTime]);

    /**
   * 스트럼 스타일 그룹 판정 함수
   * @param {number} key     - 그룹 키(동일 note.time)
   * @param {Array} group    - groupedNotesRef.current[key]
   */
  const evaluateGroup = useCallback((key, group) => {
    //console.log('🔍 evaluateGroup 시작 - key:', key, 'group:', group);
    
    // 1) 그룹 내 각 노트 점검
    let finalResult = 'Perfect';
    let totalScore = 0;
    const currentTime = currentTimeRef.current() * 1000; // 초를 밀리초로 변환
    const pressed = groupTouchedLanesRef.current[key] || new Set();

    //console.log('▶ pressed:', pressed.size);
    
    // 1) 조건 체크: 눌린 레인 수와 노트 수 검증
    // 현재 활성화된 모든 터치를 확인 (activeTouchesRef에서)
    const allActiveTouches = new Set();
    activeTouchesRef.current.forEach((laneIndex, eventKey) => {
      if (typeof laneIndex === 'number') {
        allActiveTouches.add(laneIndex);
      }
    });
    
    const pressedLaneCount = allActiveTouches.size; // 실제 활성 터치 수
    const noteCount = group.length;
    
    //console.log('▶ allActiveTouches:', allActiveTouches, 'size:', pressedLaneCount);
    //console.log('▶ noteCount:', noteCount);
    //console.log('▶ activeTouchesRef 전체:', activeTouchesRef.current);
    
    // 조건 1: 동시에 눌린 레인이 3개 이상이면 무조건 Miss
    if (pressedLaneCount >= 3) {
      //console.log('❌ 조건 1 Miss: 레인', pressedLaneCount, '개 터치 (3개 이상)');
      group.forEach(note => {
        const id = `${note.time}-${note.lane}`;
        missedNotesRef.current.add(id);
      });
      finalResult = 'Miss';
      comboRef.current = 0;
      setCombo(0);
      showJudge('Miss', group[0]);
      
      // 타임아웃·터치 기록 정리
      if (groupTimeoutsRef.current[key]) {
        clearTimeout(groupTimeoutsRef.current[key]);
        delete groupTimeoutsRef.current[key];
      }
      delete groupTouchedLanesRef.current[key];
      
      // 레인별 큐에서 이 time 제거
      group.forEach(note => {
        const q = laneQueuesRef.current[note.lane];
        if (q?.[0] === note.time) q.shift();
      });
      return;
    }
    
    // 조건 2: 노트가 1개인데 레인을 2개 이상 눌렀으면 Miss
    if (noteCount === 1 && pressedLaneCount >= 2) {
      //console.log('❌ 조건 2 Miss: 노트 1개, 레인', pressedLaneCount, '개 터치');
      group.forEach(note => {
        const id = `${note.time}-${note.lane}`;
        missedNotesRef.current.add(id);
      });
      finalResult = 'Miss';
      comboRef.current = 0;
      setCombo(0);
      showJudge('Miss', group[0]);
      
      // 타임아웃·터치 기록 정리
      if (groupTimeoutsRef.current[key]) {
        clearTimeout(groupTimeoutsRef.current[key]);
        delete groupTimeoutsRef.current[key];
      }
      delete groupTouchedLanesRef.current[key];
      
      // 레인별 큐에서 이 time 제거
      group.forEach(note => {
        const q = laneQueuesRef.current[note.lane];
        if (q?.[0] === note.time) q.shift();
      });
      return;
    }
    
    // 조건 3: 노트 수와 눌린 레인 수가 일치하지 않으면 Miss
    if (pressedLaneCount !== noteCount) {
      //console.log('❌ 조건 3 Miss: 노트', noteCount, '개, 레인', pressedLaneCount, '개 터치 (불일치)');
      group.forEach(note => {
        const id = `${note.time}-${note.lane}`;
        missedNotesRef.current.add(id);
      });
      finalResult = 'Miss';
      comboRef.current = 0;
      setCombo(0);
      showJudge('Miss', group[0]);
      
      // 타임아웃·터치 기록 정리
      if (groupTimeoutsRef.current[key]) {
        clearTimeout(groupTimeoutsRef.current[key]);
        delete groupTimeoutsRef.current[key];
      }
      delete groupTouchedLanesRef.current[key];
      
      // 레인별 큐에서 이 time 제거
      group.forEach(note => {
        const q = laneQueuesRef.current[note.lane];
        if (q?.[0] === note.time) q.shift();
      });
      return;
    }
    
    // 2) 조건을 만족하면 정상 판정 진행
    //console.log('✅ 정상 판정: 노트', noteCount, '개, 레인', pressedLaneCount, '개 터치');
    
    // 그룹 내 모든 노트의 판정 결과를 먼저 확인
    let groupPerfect = true;
    let groupMiss = false;
    
    group.forEach(note => {
      const id    = `${note.time}-${note.lane}`;
      const rawDelta = currentTime - note.time;
      
      // 비대칭 판정: 판정선 이전은 관대하게, 판정선 이후는 엄격하게
      let adjustedDelta;
      if (rawDelta < 0) {
        // 판정선 이전 (빨리 누른 경우) - 관대함
        adjustedDelta = Math.abs(rawDelta);  // 예: -50ms → 50ms
      } else {
        // 판정선 이후 (늦게 누른 경우) - 엄격함  
        adjustedDelta = Math.abs(rawDelta) + 20;  // 예: +50ms → 60ms
      }
  
      if (pressed.has(note.lane)) {
        // 누른 노트: PERFECT vs GOOD
        if (adjustedDelta <= PERFECT_WINDOW) {
          hitNotesRef.current.add(id);
        } else if (adjustedDelta <= GOOD_WINDOW) {
          hitNotesRef.current.add(id);
          groupPerfect = false; // Good이 하나라도 있으면 그룹 전체가 Perfect 아님
          finalResult = finalResult === 'Perfect' ? 'Good' : finalResult;
        } else {
          // 너무 늦게 누른 경우
          missedNotesRef.current.add(id);
          groupMiss = true;
          finalResult = 'Miss';
        }
      } else {
        // 누르지 않은 노트
        missedNotesRef.current.add(id);
        groupMiss = true;
        finalResult = 'Miss';
      }
    });
    
    // 그룹 전체가 Perfect일 때만 콤보 증가 및 점수 계산
    if (groupPerfect && !groupMiss) {
      totalScore = Math.floor(baseScore * (1 + comboRef.current / 100));
    } else if (!groupMiss) {
      // Good이 하나라도 있는 경우
      totalScore = Math.floor((baseScore * 0.5) * (1 + comboRef.current / 100));
    } else {
      // Miss가 하나라도 있는 경우
      totalScore = 0;
    }
    // // 그룹에서 가장 나쁜(절댓값이 큰) Δ를 뽑아서 전달 (테스트용)
    const deltas = group.map(note => {
      const rawDelta = currentTime - note.time;
      if (rawDelta < 0) {
        return Math.abs(rawDelta); // 판정선 이전은 관대하게
      } else {
        return Math.abs(rawDelta) + 20; // 판정선 이후는 엄격하게
      }
    });
    const worstDelta = Math.max(...deltas);
    //showJudge('Miss', group[0], worstDelta);
  
    // 2) 콤보·점수·판정 반영
    if (groupMiss) {
      // 그룹에 Miss가 하나라도 있으면 콤보 초기화
      comboRef.current = 0;
      setCombo(0);
      showJudge('Miss', group[0], worstDelta);
    } else {
      // Perfect/Good
      setScore(s => {
        const newScore = s + totalScore;
        scoreRef.current = newScore; // 점수 캡 제거 - 모든 Perfect일 때만 최대 점수 달성
        return scoreRef.current;
      });
      
      // Perfect/Good 모두 콤보 증가
      const nc = comboRef.current + 1;
      comboRef.current = nc;
      setCombo(nc);
      setMaxCombo(m => {
        maxComboRef.current = Math.max(m, nc);
        return maxComboRef.current;
      });
      
      // Perfect인 경우에만 Perfect 그룹 개수 증가
      if (groupPerfect) {
        perfectGroupsRef.current += 1;
        showJudge('Perfect', group[0], worstDelta);
      } else {
        showJudge('Good', group[0], worstDelta);
      }
    }
  
   // 3) 타임아웃·터치 기록 정리
   if (groupTimeoutsRef.current[key]) {
    clearTimeout(groupTimeoutsRef.current[key]);
    delete groupTimeoutsRef.current[key];
  }
  delete groupTouchedLanesRef.current[key];

  // 4) 레인별 큐에서 이 time 제거
  group.forEach(note => {
    const q = laneQueuesRef.current[note.lane];
    if (q?.[0] === note.time) q.shift();
  });
}, [showResult, baseScore]);


  /// 레인 클릭 판정
  const handleLaneClick = useCallback((laneIndex) => {
    if (showResult) return;
  
    // 1) 터치·클릭 피드백
    setActiveLanes(prev => {
      const s = new Set(prev);
      s.add(laneIndex);
      return s;
    });
    setTimeout(() => setActiveLanes(prev => {
      const s = new Set(prev);
      s.delete(laneIndex);
      return s;
    }), GAME_CONFIG.LANE_CLICK_DURATION);
  
    const gameTime = currentTimeRef.current() * 1000; // 오디오 기반 경과 시간 (밀리초)
  
    // 2) 이 레인의 노트 큐에서 맨 앞 시각만 확인
    const queue = laneQueuesRef.current[laneIndex];
    if (!queue?.length) return;
    const noteTime = queue[0];

    // 3) MISS_WINDOW 이내에 있지 않으면 무시
    if (Math.abs(gameTime - noteTime) > MISS_WINDOW) return;

    // 4) 이제 그룹 키와 note 객체를 가져옴
    const key   = noteTime;
    const group = groupedNotesRef.current[key];
    if (!group) return;
  
    // 5) 터치 기록
    if (!groupTouchedLanesRef.current[key]) {
      groupTouchedLanesRef.current[key] = new Set();
    }
    groupTouchedLanesRef.current[key].add(laneIndex);
  
    // 6) 조건 체크: 노트 1개인데 2개 이상 레인을 누르면 즉시 Miss
    if (group.length === 1) {
      // 현재 활성화된 모든 터치를 확인
      const allActiveTouches = new Set();
      activeTouchesRef.current.forEach((laneIndex, eventKey) => {
        if (typeof laneIndex === 'number') {
          allActiveTouches.add(laneIndex);
        }
      });
      
      // 노트 1개인데 2개 이상 레인을 누르면 Miss
      if (allActiveTouches.size >= 2) {
        //console.log('❌ 노트 1개인데 레인', allActiveTouches.size, '개 터치 - 즉시 Miss');
        group.forEach(note => {
          const id = `${note.time}-${note.lane}`;
          missedNotesRef.current.add(id);
        });
        comboRef.current = 0;
        setCombo(0);
        showJudge('Miss', group[0]);
        
        // 타임아웃·터치 기록 정리
        if (groupTimeoutsRef.current[key]) {
          clearTimeout(groupTimeoutsRef.current[key]);
          delete groupTimeoutsRef.current[key];
        }
        delete groupTouchedLanesRef.current[key];
        
        // 레인별 큐에서 이 time 제거
        group.forEach(note => {
          const q = laneQueuesRef.current[note.lane];
          if (q?.[0] === note.time) q.shift();
        });
        return;
      }
    }
  
    // 7) 스트럼 스타일 Miss 타임아웃 (GOOD_WINDOW 후)
    if (group.length > 1 && !groupTimeoutsRef.current[key]) {
      const delay = Math.max((key + GOOD_WINDOW) - gameTime, 0);
      groupTimeoutsRef.current[key] = setTimeout(() => {
        evaluateGroup(key, group);
        delete groupTimeoutsRef.current[key];
      }, delay);
    }
  
    // 8) 모든 레인 입력 시 즉시 평가 (지연 추가)
    if (groupTouchedLanesRef.current[key].size === group.length) {
      //console.log('▶ 즉시 평가 호출: groupTouchedLanes=', groupTouchedLanesRef.current[key], 'group.length=', group.length);
      if (groupTimeoutsRef.current[key]) {
        clearTimeout(groupTimeoutsRef.current[key]);
        delete groupTimeoutsRef.current[key];
      }
      
      // 즉시 평가 지연: setTimeout(() => evaluateGroup(key, group), 5) - 모든 터치가 완전히 등록된 후 판정
      setTimeout(() => evaluateGroup(key, group), 5);
    }
  
    // -- 개별 판정·점수 로직은 모두 evaluateGroup으로 이동 --
  }, [showResult, currentBeatmap, audioController, showJudge, baseScore, evaluateGroup]);
  

  // 키보드 입력
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // iOS 매너모드 대응: 사용자 상호작용 시 오디오 resume
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        try {
          audioController.unlock();
          if (!audioController.isPlaying) {
            audioController.play();
          }
        } catch (error) {
          console.warn('키보드 입력 시 오디오 resume 실패:', error);
        }
      }
      
      const laneIndex = keyMap[e.key.toLowerCase()];
      if (laneIndex !== undefined) {
        // 키보드 입력도 activeTouchesRef에 추가
        const uniqueKey = `keyboard_${e.key.toLowerCase()}`;
        activeTouchesRef.current.set(uniqueKey, laneIndex);
        
        // 약간의 지연을 두어 키보드 입력이 완전히 등록된 후 처리
        setTimeout(() => {
          handleLaneClick(laneIndex);
        }, 5);
      }
    };
    
    const handleKeyUp = (e) => {
      const laneIndex = keyMap[e.key.toLowerCase()];
      if (laneIndex !== undefined) {
        // 키보드 입력이 끝나면 activeTouchesRef에서 제거
        const uniqueKey = `keyboard_${e.key.toLowerCase()}`;
        activeTouchesRef.current.delete(uniqueKey);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [showResult]);

  // Safari 및 모바일 브라우저 대응
  React.useEffect(() => {
    const updateViewportHeight = () => {
      // Safari에서는 visualViewport.height 사용, 없으면 innerHeight 사용
      const height = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      const vh = height * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    
    updateViewportHeight();
    
    // Safari에서 주소창 숨김/표시 시에도 대응
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewportHeight);
    }
    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);
    
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateViewportHeight);
      }
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
    };
  }, []);

  // 게임이 끝나면 음악 멈추기, 다음 라운드 준비
  React.useEffect(() => {
    if (showResult) {
      // 라운드 종료 시 오디오 리셋 (stop + reset 대신 reset만 사용)
      audioController.reset();
    } else {
      // 새로운 라운드 시작 시 게임 상태 리셋
      hitNotesRef.current.clear();
      missedNotesRef.current.clear();
      setScore(0);
      setCombo(0);
      setMaxCombo(0);
      comboRef.current = 0;
      setShowCombo(true);
    }
  }, [showResult]);

  // 브라우저를 벗어나면 해당 라운드 종료
  React.useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && !showResult) {
        // 브라우저를 벗어나면 현재 라운드 종료
        audioController.stop();
        onGameEnd({
          score: scoreRef.current,
          combo: maxComboRef.current,
          timestamp: toKstIso(Timestamp.now())
        });
        setShowResult(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pagehide', handleVisibility);
    window.addEventListener('beforeunload', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', handleVisibility);
      window.removeEventListener('beforeunload', handleVisibility);
    };
  }, [showResult]);

  // 게임 루프에서 마지막 노트가 끝난 뒤, showCombo를 false로
  useEffect(() => {
    if (!showResult) {
      // 마지막 노트의 time + GOOD_WINDOW
      const lastNote = currentBeatmap[currentBeatmap.length - 1];
      const lastNoteEnd = lastNote.time + GOOD_WINDOW;
      const now = (audioController.getCurrentTime() || 0) * 1000; // 밀리초로 변환
      if (now > lastNoteEnd) {
        setTimeout(() => setShowCombo(false), GAME_CONFIG.JUDGE_MESSAGE_DURATION);
      }
    } else {
      setShowCombo(false);
    }
  }, [showResult]);


  // 멀티터치 지원 핸들러
  const handleLanePress = (laneIndex) => (e) => {
    //console.log('▶ handleLanePress: laneIndex=', laneIndex, 'e.pointerId=', e.pointerId, 'e.pointerType=', e.pointerType);
    e.preventDefault(); // 터치 이벤트 제대로 처리
    
    // iOS 매너모드 대응: 사용자 상호작용 시 오디오 resume
    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      try {
        audioController.unlock();
        if (!audioController.isPlaying) {
          audioController.play();
        }
      } catch (error) {
        //console.warn('레인 터치 시 오디오 resume 실패:', error);
      }
    }
    
    // 터치 ID 추적 (멀티터치 지원)
    if (e.pointerId !== undefined && e.pointerId !== null) {
      activeTouchesRef.current.set(e.pointerId, laneIndex);
      //console.log('▶ 터치 추가: pointerId=', e.pointerId, 'laneIndex=', laneIndex);
    } else {
      // pointerId가 없는 경우 (터치 이벤트 등) 고유 키 생성
      const uniqueKey = `touch_${Date.now()}_${laneIndex}_${Math.random()}`;
      activeTouchesRef.current.set(uniqueKey, laneIndex);
      //console.log('▶ 터치 추가: uniqueKey=', uniqueKey, 'laneIndex=', laneIndex);
    }
    
    //console.log('▶ handleLanePress 후 activeTouchesRef:', activeTouchesRef.current);
    
    setActiveLanes(prev => {
      const newSet = new Set(prev);
      newSet.add(laneIndex);
      return newSet;
    });
    
    // 터치 입력도 키보드와 동일하게 즉시 처리
    handleLaneClick(laneIndex);
  };

  const handleLaneRelease = (laneIndex) => (e) => {
    e.preventDefault(); // 터치 이벤트 제대로 처리
    
    //console.log('▶ handleLaneRelease: laneIndex=', laneIndex, 'e.pointerId=', e.pointerId);
    
    // 터치 ID 제거
    if (e.pointerId !== undefined) {
      activeTouchesRef.current.delete(e.pointerId);
    }
    
    //console.log('▶ handleLaneRelease 후 activeTouchesRef:', activeTouchesRef.current);
    
    setActiveLanes(prev => {
      const newSet = new Set(prev);
      newSet.delete(laneIndex);
      return newSet;
    });
  };

  const handleLaneCancel = (laneIndex) => (e) => {
    e.preventDefault();
    
    // 터치 ID 제거
    if (e.pointerId !== undefined) {
      activeTouchesRef.current.delete(e.pointerId);
    }
    
    setActiveLanes(prev => {
      const newSet = new Set(prev);
      newSet.delete(laneIndex);
      return newSet;
    });
  };
  
  // Lane UI (키 라벨) - 멀티터치 지원
  const renderLanes = () => Array.from({ length: 4 }, (_, index) => (
    <Lane
      key={index}
      onPointerDown={handleLanePress(index)}
      onPointerUp={handleLaneRelease(index)}
      onPointerCancel={handleLaneCancel(index)}
      style={{
        backgroundColor: activeLanes.has(index) ? 'rgba(255,255,255,0.12)' : 'transparent',
      }}
    >
      <KeyLabel>{keyLabels[index]}</KeyLabel>
    </Lane>
  ));

  return (
    <GameContainer>
      {judgeMessage && (
        <JudgeMessage result={judgeMessage} key={judgeKey}>{judgeMessage}</JudgeMessage>
      )}
      {combo > 1 && showCombo && !showResult && (
        <CurrentCombo>{combo} COMBO</CurrentCombo>
      )}
      {!showResult && (
        <>
          <RoundInfo>Round {currentRound}/{totalRounds}</RoundInfo>
          <ModeInfo>{gameMode.toUpperCase()}</ModeInfo>
        </>
      )}
      <GameArea ref={gameAreaRef}>
        {!showResult && (
          <CanvasContainer>
            <GameCanvas
              ref={canvasRef}
              width={canvasWidth}
              height={canvasHeight}
            />
          </CanvasContainer>
        )}
        <LaneContainer>
          {renderLanes()}
        </LaneContainer>
        {/* 결과 화면은 EntryForm 등 상위 컴포넌트에서만 보여주므로, RhythmGame 내부의 ResultScreen은 삭제 */}
      </GameArea>
    </GameContainer>
  );
});

export default RhythmGame;